
import {
    Form,
    FormField,
    FormSection,
    FormType,
    InputType,
    FilteredValue,
    DropdownFilterContext,
    ReactiveComputedFieldContext,
    ReactiveFieldLogicContext,
    ReactiveFormChangedField,
    ReactiveFormValue,
    type ReactiveDropdownDataSource,
} from '@fitogest/reactive-form-builder/interfaces';
import { ReactiveSelectionResolver, type ReactiveSelectionResolverConfiguration } from '@fitogest/reactive-form-builder/utils';
import { CamposListsDefinition } from './list-variables';
import { CamposEnvironmentDefinition } from '../environment-variables';
import { VisitasVariedadMesModel } from 'src/app/models/visitas_variedad_mes/visitas_variedad_mes.model';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { FincasModel } from 'src/app/models/form-common/fincas.module';


const FINCA_SELECTION_CONFIGURATION: ReactiveSelectionResolverConfiguration = {
  idKeys: ['id', 'id_finca', 'nombre'],
};

const CULTIVO_SELECTION_CONFIGURATION: ReactiveSelectionResolverConfiguration = {
  idKeys: ['id', 'id_cultivo', 'id_variedad', 'codigo_cultivo', 'codigo_variedad', 'cultivo'],
};

const VARIEDAD_SELECTION_CONFIGURATION: ReactiveSelectionResolverConfiguration = {
  idKeys: ['id', 'id_cultivo', 'id_variedad', 'codigo_cultivo', 'codigo_variedad', 'variedad'],
};

export interface CamposDropdownEndpoints {
  readonly cultivos?: unknown;
  readonly variedades?: unknown;
}

export class CamposFormDefinition {
  public formType!: FormType;

  public env: CamposEnvironmentDefinition = new CamposEnvironmentDefinition();

  public lists: CamposListsDefinition = new CamposListsDefinition();

  public model: ReactiveFormValue<ParcelasModel> = {} as ReactiveFormValue<ParcelasModel>;

  public tag: ReactiveFormChangedField<ParcelasModel> = '';

  public formFieldsSection!: FormSection<object>;

  public formFieldsTitular!: FormSection<object>;

  public formFieldsResponsable!: FormSection<object>;

  public formFields!: Form<ParcelasModel>;

  public formFieldsCultivos!: Form<VisitasVariedadMesModel>;

  public fieldNames: FormField<any>[] = [];

  public fieldLabels: string[] = [];

  public openDialog = false;

  public mapaAbierto = false;

  public origin = 'parcelas-form';

  public finca = 'finca';

  public localidad = 'localidad';

  public cultivo = 'cultivo';

  public variedad = 'variedad';

  public parcelaNombre = 'nombre Campo';

  public mapaOpened = false;

  constructor(
    type: FormType,
    _model: Record<string,string>,
    table: (string[] | string[][])[] | null,
    private readonly dropdownEndpoints: CamposDropdownEndpoints = {}
  ) {
    this.formType = +type;

    this.formFields = {
      name: 'campos',
      header: 'Datos del Campo',
      type: this.formType,
      sections: this.getFormSections(),
      table: { title: '', showIndex: false, data: table?.[0], style: table?.[1]},
      uploadButton: {
        isEnabled: false,
        label: ''
      },
      signed: false
    };

    this.formFieldsCultivos = {
      name: 'visitas_variedad_mes',
      header: '',
      type: FormType.CREATION,
      sections: this.getFormSectionsCultivos(),
      table: { title: '', showIndex: false, data: table?.[0], style: table?.[1]},
      uploadButton: {
        isEnabled: false,
        label: ''
      },
      signed: false
    };
  }



  private createCultivosDataSourceField(): Partial<Pick<FormField<ParcelasModel>, 'dataSource'>> {
    const endpoint = this.dropdownEndpoints.cultivos;

    if (endpoint === undefined || endpoint === null) {
      return {};
    }

    const dataSource: ReactiveDropdownDataSource<ParcelasModel> = {
      endpoint,
      cacheKey: 'campos-cultivos',
      labelKey: 'cultivo',
      valueKey: 'id',
      distinctBy: 'id',
      cacheEmptyResults: false,
    };

    return { dataSource };
  }

  private createVariedadesDataSourceField(): Partial<Pick<FormField<ParcelasModel>, 'dataSource' | 'dependencyBehaviour'>> {
    const endpoint = this.dropdownEndpoints.variedades;

    if (endpoint === undefined || endpoint === null) {
      return {};
    }

    const dataSource: ReactiveDropdownDataSource<ParcelasModel> = {
      endpoint,
      cacheKey: 'campos-variedades',
      labelKey: 'variedad',
      valueKey: 'id',
      distinctBy: 'id',
      dependsOn: ['id_cultivo', 'cultivo'],
      cacheEmptyResults: false,
      filterOption: (option, context) => {
        const selectedCultivo = this.resolveSelectedCultivo(context);

        if (selectedCultivo === null) {
          return true;
        }

        const raw = option.raw ?? option.value;
        const variedad = raw as Partial<VisitasVariedadMesModel>;

        return variedad.cultivo === selectedCultivo.cultivo;
      },
    };

    return {
      dataSource,
      dependencyBehaviour: {
        filterLocally: true,
        clearOnParentChange: true,
        keepCurrentValueIfStillAvailable: true,
      },
    };
  }

  public getFormFieldsDatosParcela(): FormField<any>[] {
    const formFieldsDatosParcela: FormField<any>[] = [
        {
            name: 'nombre',
            label: 'Nombre',
            input: {type: InputType.TEXT, isEditable: true},
            width: 3,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: true,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true,
            },
            order: 1
        },
        {
            name: 'superficie_sigpac',
            label: 'Sup.\xa0Sigpac\xa0' + this.env.uds_medida,
            input: {type: InputType.TEXT, isEditable: true},
            width: 3,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: true,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true,
            },
            order: 2
        },
        {
            name: 'superficie_cultivada',
            label: 'Sup. Cultivada\xa0' + this.env.uds_medida,
            input: {type: InputType.TEXT, isEditable: true},
            width: 3,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: true,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true,
            },
            order: 3
        },
        {
            name: this.formType < 3 ? 'id_cultivo' : 'cultivo',
            label: 'Cultivo',
            input: {
                type: InputType.DROPDOWN_SEARCH,
                isEditable: true,
                data: this.lists.cultivos,
                dropdownConfiguration: {
                    valuePrimaryKey: 'id',
                }
            },
            width: 3,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: true,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true,
            },
            ...this.createCultivosDataSourceField(),
            buttons: [
                {
                    class: 'btn btn-success border rounded-circle shadow-sm p-0 d-flex align-items-center justify-content-center',
                    style: `
                        width: 26px;
                        height: 26px;
                        margin-left: 6px;
                        font-size: .95rem;
                        transform: translateY(-0.5rem);
                    `,
                    icon: 'fa fa-plus',
                    label: '',
                    tooltip: 'Añadir nuevo valor',
                    tooltipPosition: 'top',
                    function: () => this.openDialog = true
                }
            ],
            order: 4
        },
        {
            name: this.formType < 3 ? 'id_variedad' : 'variedad',
            label: 'Variedad',
            input: {
                type: InputType.DROPDOWN,
                isEditable: true,
                data: this.lists.variedades,
                dropdownConfiguration: {
                    valuePrimaryKey: 'id',
                    filter: (
                        it: FilteredValue<VisitasVariedadMesModel>,
                        context?: DropdownFilterContext<VisitasVariedadMesModel>,
                    ) => it.value?.cultivo === this.resolveSelectedCultivo(context)?.cultivo,
                }
            },
            width: 3,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: true,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true,
            },
            order: 5,
            ...this.createVariedadesDataSourceField()
        },
        {
            name: 'marco',
            label: 'Marco\xa0(ancho\xa0x\xa0largo)',
            input: {
                type: InputType.TWO_FIELDS_CONCAT,
                isEditable: true,
                twoFieldsConcatConfiguration: {
                    subField1: 'marco_ancho',
                    separator: 'x',
                    subField2: 'marco_largo',
                }
            },
            width: 3,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: true,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true,
            },
            order: 6
        },
        {
            name: 'anyo_plantacion',
            label: 'Año\xa0de\xa0plantación',
            input: {type: InputType.TEXT, isEditable: true},
            width: 3,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: false,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true,
            },
            order: 7
        },
        {
            name: 'plantas',
            label: 'Nº\xa0Árboles/Plantas',
            input: {type: InputType.TEXT, isEditable: true},
            width: 3,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: false,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true,
            },
            order: 8
        },
        {
            name: 'patron',
            label: this.model.cultivo === 'VIÑA' ? 'Portainjertos' : 'Patrón',
            input: {
                type: InputType.DROPDOWN_SEARCH,
                isEditable: true,
                data: this.lists.patron,
                dropdownConfiguration: {
                    swap: (it: FormField<object>, context?: ReactiveFieldLogicContext<object>) => {
                        const cultivo = this.resolveSelectedCultivo(context)?.cultivo ?? this.model.cultivo ?? '';
                        const isVineyard = cultivo.toUpperCase() === 'VIÑA';

                        if (isVineyard) {
                            it.label = 'Portainjertos';
                        } else if (this.env.appName === 'momparler') {
                            it.label = 'Formato';
                        } else {
                            it.label = 'Patrón';
                        }

                        it.input.data = this.getPatron(cultivo);
                    },
                }
            },
            updateValue: (it: FormField<object>) => {
                if (this.model.cultivo === 'VIÑA') {
                    it.label = 'Portainjertos';
                } else {
                    it.label = 'Patrón';
                }
            },
            width: 3,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: false,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true,
            },
            order: 9
        }
    ];

    this.fieldNames = this.fieldNames.concat(formFieldsDatosParcela.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(formFieldsDatosParcela.filter((it) => it.conditions.isVisible).map(it => it.label));
    return formFieldsDatosParcela.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  public getFormFieldsDatosSigpac(): FormField<any>[] {
    const formFieldsDatosSigpac: FormField<any>[] = [
        {
            name: 'cod_provincia',
            label: 'Cod. Provincia',
            input: {type: InputType.TEXT, isEditable: true},
            width: 3,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: false,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true,
            },
            order: 1
        },
        {
            name: 'municipio',
            label: 'Municipio',
            input: {type: InputType.TEXT, isEditable: true},
            width: 3,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: false,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true,
            },
            order: 2
        },
        {
            name: 'poligono',
            label: 'Polígono',
            input: {type: InputType.TEXT, isEditable: true},
            width: 3,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: false,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true,
            },
            order: 3
        },
        {
            name: 'numero',
            label: 'Nº Parcela',
            input: {type: InputType.TEXT, isEditable: true},
            width: 3,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: false,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true,
            },
            order: 4
        },
        {
            name: 'recinto',
            label: 'Recinto',
            input: {type: InputType.TEXT, isEditable: true},
            width: 3,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: false,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true,
            },
            order: 5
        }
    ];

    this.fieldNames = this.fieldNames.concat(formFieldsDatosSigpac.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(formFieldsDatosSigpac.filter((it) => it.conditions.isVisible).map(it => it.label));
    return formFieldsDatosSigpac.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  public getFormFieldsCultivos(): FormField<any>[] {
    const formFieldsCultivos: FormField<any>[] = [
        {
            name: 'cultivo',
            label: 'Cultivo',
            input: {type: InputType.TEXT, isEditable: true},
            width: 6,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: true,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true,
            },
            order: 1
        },
        {
            name: 'variedad',
            label: 'Variedad',
            input: {type: InputType.TEXT, isEditable: true},
            required: true,
            width: 6,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: true,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true,
            },
            order: 2
        },
    ];

    this.fieldNames = this.fieldNames.concat(formFieldsCultivos.filter((it) => it.conditions.isVisible));
    this.fieldLabels = this.fieldLabels.concat(formFieldsCultivos.filter((it) => it.conditions.isVisible).map(it => it.label));
    return formFieldsCultivos.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }


    public getHiddenFields(): FormField<any>[] {
      const hiddenFields: FormField<any>[] = [
        {
          name: 'id',
          label: '',
          input: {
            type: InputType.HIDDEN,
            defaultValue: this.model.id,
            isEditable: false
          },
          width: 2,
          conditions: {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
          },
          order: 1
        },
            {
                name: 'id_cliente',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.resolveClientId(),
                    isEditable: false
                },
                computed: {
                    dependsOn: ['id_finca', 'finca'],
                    resolve: (context: ReactiveComputedFieldContext<ParcelasModel>) => this.resolveClientId(context),
                },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.hasClients
                },
                order: 2
            },
            {
                name: 'id_usuario',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.resolveUserId(),
                    isEditable: false
                },
                computed: {
                    dependsOn: ['id_finca', 'finca'],
                    resolve: (context: ReactiveComputedFieldContext<ParcelasModel>) => this.resolveUserId(context),
                },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 3
            },
            {
                name: 'cultivo',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                computed: {
                    dependsOn: ['id_cultivo', 'cultivo'],
                    resolve: (context: ReactiveComputedFieldContext<ParcelasModel>) => {
                        return this.resolveSelectedCultivo(context)?.cultivo ?? this.model.cultivo ?? '';
                    },
                },
                order: 4
            },
            {
                name: 'variedad',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                computed: {
                    dependsOn: ['id_variedad', 'variedad'],
                    resolve: (context: ReactiveComputedFieldContext<ParcelasModel>) => {
                        return this.resolveSelectedVariedad(context)?.variedad ?? this.model.variedad ?? '';
                    },
                },
                order: 5
            },
      ];

      this.fieldNames = this.fieldNames.concat(hiddenFields.filter((it) => it.conditions.isVisible));
      this.fieldLabels = this.fieldLabels.concat(hiddenFields.filter((it) => it.conditions.isVisible).map(it => it.label));
      return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private resolveClientId(context?: ReactiveComputedFieldContext<ParcelasModel>): unknown {
        if (this.env.cliente?.id) {
            return this.env.cliente.id;
        }

        return this.resolveSelectedFinca(context)?.id_cliente ?? this.model.id_cliente ?? '';
    }

    private resolveUserId(context?: ReactiveComputedFieldContext<ParcelasModel>): unknown {
        if (!this.env.showTareasMto) {
            return this.env.usuario?.id ?? this.resolveSelectedFinca(context)?.id_usuario ?? '';
        }

        return !FormType.EDITION ? this.env.usuario.id : '';
    }

    private resolveSelectedFinca(context?: ReactiveComputedFieldContext<ParcelasModel>): FincasModel | null {
        const selectedValue = this.resolveContextValue(context, 'id_finca') ?? this.resolveContextValue(context, 'finca')
            ?? this.model.id_finca ?? this.model.finca;

        return ReactiveSelectionResolver.resolveSelectedEntities(
            this.lists.fincas,
            selectedValue,
            FINCA_SELECTION_CONFIGURATION,
        )[0] ?? null;
    }

    private resolveSelectedCultivo(context?: unknown): VisitasVariedadMesModel | null {
        const selectedValue = this.resolveContextValue(context, 'id_cultivo') ?? this.resolveContextValue(context, 'cultivo')
            ?? this.model.id_cultivo ?? this.model.cultivo;

        return ReactiveSelectionResolver.resolveSelectedEntities(
            this.lists.cultivos,
            selectedValue,
            CULTIVO_SELECTION_CONFIGURATION,
        )[0] ?? null;
    }

    private resolveSelectedVariedad(context?: unknown): VisitasVariedadMesModel | null {
        const selectedValue = this.resolveContextValue(context, 'id_variedad') ?? this.resolveContextValue(context, 'variedad')
            ?? this.model.id_variedad ?? this.model.variedad;

        return ReactiveSelectionResolver.resolveSelectedEntities(
            this.lists.variedades,
            selectedValue,
            VARIEDAD_SELECTION_CONFIGURATION,
        )[0] ?? null;
    }

    private resolveContextValue(context: unknown, fieldName: string): unknown {
        return ReactiveSelectionResolver.readContextValue(context, fieldName);
    }


  private getFormSections(): FormSection<any>[] {
    const sections: FormSection<any>[] = [
      {
        name: '',
        width: 12,
        isVisible: true,
        fields: this.getFormFieldsDatosParcela(),
        order: 1
      },
      {
        name: 'Datos SigPAC',
        width: 12,
        isVisible: true,
        fields: this.getFormFieldsDatosSigpac(),
        buttons: [{
            class: 'btn btn-warning text-nowrap',
            style: 'margin-left:0px !important',
            icon: 'fa fa-search',
            label: ' Mapa',
            isVisible: this.env.appName !== 'agrogestSIEX',
            function: () => this.abreMapa()
        }],
        order: 2
      },
      {
        name: '',
        width: 12,
        isVisible: true,
        fields: this.getHiddenFields(),
        order: 3
      },
    ];

    return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  private getFormSectionsCultivos(): FormSection<any>[] {
    const sections: FormSection<any>[] = [
      {
        name: '',
        width: 12,
        isVisible: true,
        fields: this.getFormFieldsCultivos(),
        order: 1
      },
    ];

    return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

    private getPatron(cultivo: string) {
        switch (cultivo.toLowerCase()) {
            case 'cerezo':          return this.lists.patronCerezo;
            case 'ciruelo':         return this.lists.patronCiruelo;
            case 'mandarinos':      return this.lists.patronCitricos;
            case 'naranjos':        return this.lists.patronCitricos;
            case 'lima':            return this.lists.patronCitricos;
            case 'limoneros':       return this.lists.patronCitricos;
            case 'pomelo':          return this.lists.patronCitricos;
            case 'pomelos':         return this.lists.patronCitricos;
            case 'manzano':         return this.lists.patronManzano;
            case 'pera':            return this.lists.patronPera;
            case 'peral':           return this.lists.patronPera;
            case 'viña':            return this.lists.patronVina;
            case 'almendro':        return this.lists.patronAlmendro;
            case 'melocotonero':    return this.lists.patronMelocotonero;
            default:                return this.lists.patron;
        }
    }

    private abreMapa() {
        this.mapaOpened = true;

        if (
            !this.model.nombre ||
            !this.model.id_cultivo ||
            !this.model.id_variedad
        ) {
            alert ('Debe rellenar los campos obligatorios antes de abrir el Mapa.');
            return;
        }

        this.mapaAbierto = !this.mapaAbierto;
        this.finca = this.model.nombre || '';
        this.cultivo = this.model.cultivo || '';
        this.variedad = this.model.variedad || '';

        this.parcelaNombre = this.model.nombre || '';
    }
}